import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Rocket, GraduationCap, Sword, Trophy, Type, ArrowLeft, HelpCircle, BookOpen, RefreshCw, Shuffle, Volume2, VolumeX, SkipForward, Home, X, Check, Zap, Clock, Star, Target, Info, Music, Settings, Award, TrendingUp, User, School, Shield, LogOut, Plus, Upload, Download, Edit, Trash2, Save, FileText, Book } from 'lucide-react';
import { Howl } from 'howler';
import { ASSETS } from './assetManifest.js';
import { CONSTANTS } from './data.js';

// Audio Manager Class
class AudioManager {
  constructor() {
    this.bgmTracks = {};
    this.sfxSounds = {};
    this.currentBGM = null;
    this.gameOverBGM = null;
    this.bgmVolume = parseFloat(localStorage.getItem('bgmVolume') || CONSTANTS.AUDIO.DEFAULT_BGM_VOLUME);
    this.sfxVolume = parseFloat(localStorage.getItem('sfxVolume') || CONSTANTS.AUDIO.DEFAULT_SFX_VOLUME);
    this.bgmMuted = localStorage.getItem('bgmMuted') === 'true';
    this.sfxMuted = localStorage.getItem('sfxMuted') === 'true';
    this.currentTrack = localStorage.getItem('selectedBGM') || 'epic_orchestral';
    this.currentSpeedMultiplier = 1.0;
    
    this.initializeAudio();
  }

  initializeAudio() {
    // Initialize BGM tracks
    CONSTANTS.AUDIO.BGM_TRACKS.forEach(track => {
      this.bgmTracks[track.id] = new Howl({
        src: [ASSETS.sounds[track.id].path],
        loop: true,
        volume: this.bgmMuted ? 0 : this.bgmVolume,
        preload: true
      });
    });

    // Initialize game over BGM separately
    this.gameOverBGM = new Howl({
      src: [ASSETS.sounds.game_over_music.path],
      loop: false,
      volume: this.bgmMuted ? 0 : this.bgmVolume,
      preload: true
    });

    // Initialize SFX sounds
    Object.values(CONSTANTS.AUDIO.SFX_EVENTS).forEach(sfxKey => {
      if (ASSETS.sounds[sfxKey]) {
        this.sfxSounds[sfxKey] = new Howl({
          src: [ASSETS.sounds[sfxKey].path],
          volume: this.sfxMuted ? 0 : this.sfxVolume,
          preload: true
        });
      }
    });
  }

  playBGM(trackId, speedMultiplier = 1.0) {
    if (this.currentBGM) {
      this.currentBGM.stop();
    }
    
    // Stop game over BGM if it's playing
    if (this.gameOverBGM) {
      this.gameOverBGM.stop();
    }
    
    this.currentTrack = trackId;
    this.currentSpeedMultiplier = speedMultiplier;
    localStorage.setItem('selectedBGM', trackId);
    
    if (this.bgmTracks[trackId] && !this.bgmMuted) {
      this.currentBGM = this.bgmTracks[trackId];
      this.currentBGM.rate(speedMultiplier);
      this.currentBGM.play();
    }
  }

  updateBGMSpeed(speedMultiplier) {
    this.currentSpeedMultiplier = speedMultiplier;
    if (this.currentBGM && this.currentBGM.playing()) {
      this.currentBGM.rate(speedMultiplier);
    }
  }

  stopBGM() {
    if (this.currentBGM) {
      this.currentBGM.stop();
      this.currentBGM = null;
    }
  }

  stopAllBGM() {
    if (this.currentBGM) {
      this.currentBGM.stop();
      this.currentBGM = null;
    }
    if (this.gameOverBGM) {
      this.gameOverBGM.stop();
    }
  }

  playGameOverBGM() {
    // Stop current BGM first
    this.stopBGM();
    
    // Play game over BGM
    if (this.gameOverBGM && !this.bgmMuted) {
      this.gameOverBGM.play();
    }
  }

  playSFX(sfxKey) {
    if (this.sfxSounds[sfxKey] && !this.sfxMuted) {
      this.sfxSounds[sfxKey].play();
    }
  }

  setBGMVolume(volume) {
    this.bgmVolume = volume;
    localStorage.setItem('bgmVolume', volume.toString());
    Object.values(this.bgmTracks).forEach(track => {
      track.volume(this.bgmMuted ? 0 : volume);
    });
    if (this.gameOverBGM) {
      this.gameOverBGM.volume(this.bgmMuted ? 0 : volume);
    }
  }

  setSFXVolume(volume) {
    this.sfxVolume = volume;
    localStorage.setItem('sfxVolume', volume.toString());
    Object.values(this.sfxSounds).forEach(sound => {
      sound.volume(this.sfxMuted ? 0 : volume);
    });
  }

  toggleBGMMute() {
    this.bgmMuted = !this.bgmMuted;
    localStorage.setItem('bgmMuted', this.bgmMuted.toString());
    Object.values(this.bgmTracks).forEach(track => {
      track.volume(this.bgmMuted ? 0 : this.bgmVolume);
    });
    if (this.gameOverBGM) {
      this.gameOverBGM.volume(this.bgmMuted ? 0 : this.bgmVolume);
    }
  }

  toggleSFXMute() {
    this.sfxMuted = !this.sfxMuted;
    localStorage.setItem('sfxMuted', this.sfxMuted.toString());
    Object.values(this.sfxSounds).forEach(sound => {
      sound.volume(this.sfxMuted ? 0 : this.sfxVolume);
    });
  }
}

// Global audio manager instance
const audioManager = new AudioManager();

// Admin Manager Class
class AdminManager {
  constructor() {
    this.storageKey = CONSTANTS.ADMIN.STORAGE_KEY;
  }

  login(username, password) {
    if (username === CONSTANTS.ADMIN.CREDENTIALS.username && 
        password === CONSTANTS.ADMIN.CREDENTIALS.password) {
      const session = {
        role: CONSTANTS.ADMIN.ROLES.SUPER_ADMIN,
        loginTime: Date.now(),
        username: username
      };
      localStorage.setItem(this.storageKey, JSON.stringify(session));
      return true;
    }
    return false;
  }

  logout() {
    localStorage.removeItem(this.storageKey);
  }

  getCurrentSession() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) return null;
      
      const session = JSON.parse(stored);
      const now = Date.now();
      
      // Check if session has expired
      if (now - session.loginTime > CONSTANTS.ADMIN.SESSION_DURATION) {
        this.logout();
        return null;
      }
      
      return session;
    } catch (error) {
      console.error('Error loading admin session:', error);
      return null;
    }
  }

  isLoggedIn() {
    return this.getCurrentSession() !== null;
  }

  isSuperAdmin() {
    const session = this.getCurrentSession();
    return session && session.role === CONSTANTS.ADMIN.ROLES.SUPER_ADMIN;
  }
}

// Question Bank Manager Class
class QuestionBankManager {
  constructor() {
    this.storageKey = CONSTANTS.QUESTION_BANK.STORAGE_KEY;
    this.initializeDefaultQuestions();
  }

  initializeDefaultQuestions() {
    const existing = this.getQuestions();
    if (Object.keys(existing).length === 0) {
      localStorage.setItem(this.storageKey, JSON.stringify(CONSTANTS.QUESTION_BANK.DEFAULT_QUESTIONS));
    }
  }

  getQuestions() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : CONSTANTS.QUESTION_BANK.DEFAULT_QUESTIONS;
    } catch (error) {
      console.error('Error loading questions:', error);
      return CONSTANTS.QUESTION_BANK.DEFAULT_QUESTIONS;
    }
  }

  getQuestionsByMode(mode) {
    const questions = this.getQuestions();
    return questions[mode] || [];
  }

  addQuestion(mode, question) {
    const questions = this.getQuestions();
    if (!questions[mode]) {
      questions[mode] = [];
    }
    
    // Add unique ID
    question.id = Math.random().toString(36).substr(2, 9);
    questions[mode].push(question);
    
    localStorage.setItem(this.storageKey, JSON.stringify(questions));
    return question;
  }

  updateQuestion(mode, questionId, updatedQuestion) {
    const questions = this.getQuestions();
    if (questions[mode]) {
      const index = questions[mode].findIndex(q => q.id === questionId);
      if (index !== -1) {
        questions[mode][index] = { ...updatedQuestion, id: questionId };
        localStorage.setItem(this.storageKey, JSON.stringify(questions));
        return true;
      }
    }
    return false;
  }

  deleteQuestion(mode, questionId) {
    const questions = this.getQuestions();
    if (questions[mode]) {
      questions[mode] = questions[mode].filter(q => q.id !== questionId);
      localStorage.setItem(this.storageKey, JSON.stringify(questions));
      return true;
    }
    return false;
  }

  importQuestionsFromExcel(mode, excelData) {
    const questions = this.getQuestions();
    if (!questions[mode]) {
      questions[mode] = [];
    }

    const importedQuestions = excelData.map(row => ({
      ...row,
      id: Math.random().toString(36).substr(2, 9)
    }));

    questions[mode] = [...questions[mode], ...importedQuestions];
    localStorage.setItem(this.storageKey, JSON.stringify(questions));
    return importedQuestions.length;
  }

  getRandomQuestion(mode, level) {
    const questions = this.getQuestionsByMode(mode);
    const filteredQuestions = level ? 
      questions.filter(q => q.level === level) : 
      questions;
    
    if (filteredQuestions.length === 0) {
      // Fallback to default questions if no custom questions available
      const defaultQuestions = CONSTANTS.QUESTION_BANK.DEFAULT_QUESTIONS[mode] || [];
      const defaultFiltered = level ? 
        defaultQuestions.filter(q => q.level === level) : 
        defaultQuestions;
      
      if (defaultFiltered.length === 0) return null;
      return defaultFiltered[Math.floor(Math.random() * defaultFiltered.length)];
    }
    
    return filteredQuestions[Math.floor(Math.random() * filteredQuestions.length)];
  }
}

// Global instances
const adminManager = new AdminManager();
const questionBankManager = new QuestionBankManager();

// Player Info Manager Class
class PlayerInfoManager {
  constructor() {
    this.storageKey = CONSTANTS.PLAYER_INFO.STORAGE_KEY;
  }

  savePlayerInfo(playerInfo) {
    localStorage.setItem(this.storageKey, JSON.stringify(playerInfo));
  }

  getPlayerInfo() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : {
        name: CONSTANTS.PLAYER_INFO.DEFAULT_NAME,
        class: CONSTANTS.PLAYER_INFO.DEFAULT_CLASS
      };
    } catch (error) {
      console.error('Error loading player info:', error);
      return {
        name: CONSTANTS.PLAYER_INFO.DEFAULT_NAME,
        class: CONSTANTS.PLAYER_INFO.DEFAULT_CLASS
      };
    }
  }

  validatePlayerInfo(name, className) {
    const errors = [];
    
    if (!name || name.trim().length === 0) {
      errors.push(CONSTANTS.PLAYER_INFO.VALIDATION_MESSAGES.NAME_REQUIRED);
    } else if (name.trim().length < CONSTANTS.PLAYER_INFO.MIN_NAME_LENGTH) {
      errors.push(CONSTANTS.PLAYER_INFO.VALIDATION_MESSAGES.NAME_TOO_SHORT);
    } else if (name.trim().length > CONSTANTS.PLAYER_INFO.MAX_NAME_LENGTH) {
      errors.push(CONSTANTS.PLAYER_INFO.VALIDATION_MESSAGES.NAME_TOO_LONG);
    }

    if (!className || className.trim().length === 0) {
      errors.push(CONSTANTS.PLAYER_INFO.VALIDATION_MESSAGES.CLASS_REQUIRED);
    } else if (className.trim().length < CONSTANTS.PLAYER_INFO.MIN_CLASS_LENGTH) {
      errors.push(CONSTANTS.PLAYER_INFO.VALIDATION_MESSAGES.CLASS_TOO_SHORT);
    } else if (className.trim().length > CONSTANTS.PLAYER_INFO.MAX_CLASS_LENGTH) {
      errors.push(CONSTANTS.PLAYER_INFO.VALIDATION_MESSAGES.CLASS_TOO_LONG);
    }

    return errors;
  }
}

// Global player info manager instance
const playerInfoManager = new PlayerInfoManager();

// Scoreboard Manager Class
class ScoreboardManager {
  constructor() {
    this.storageKey = CONSTANTS.SCOREBOARD.STORAGE_KEY;
    this.maxEntries = CONSTANTS.SCOREBOARD.MAX_ENTRIES;
  }

  saveScore(scoreData) {
    const scores = this.getScores();
    const newScore = {
      ...scoreData,
      timestamp: Date.now(),
      id: Math.random().toString(36).substr(2, 9)
    };
    
    scores.push(newScore);
    scores.sort((a, b) => b.score - a.score);
    
    // Keep only max entries
    const trimmedScores = scores.slice(0, this.maxEntries);
    
    localStorage.setItem(this.storageKey, JSON.stringify(trimmedScores));
    return newScore;
  }

  getScores() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading scores:', error);
      return [];
    }
  }

  clearScores() {
    localStorage.removeItem(this.storageKey);
  }

  getTopScores(limit = 10) {
    return this.getScores().slice(0, limit);
  }
}

// Global scoreboard manager instance
const scoreboardManager = new ScoreboardManager();

// Admin Login Modal Component
const AdminLoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleLogin = () => {
    if (adminManager.login(username, password)) {
      onLoginSuccess();
      onClose();
      setUsername('');
      setPassword('');
      setError('');
    } else {
      setError('Invalid credentials');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gray-800 rounded-xl max-w-md w-full p-6"
      >
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-red-500/20 rounded-full mx-auto mb-4 flex items-center justify-center">
            <Shield size={32} className="text-red-400" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Super Admin Login</h3>
          <p className="text-gray-400">Enter lecturer credentials to access admin features</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-white font-medium mb-2">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter username"
              className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-red-400 focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-white font-medium mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter password"
              className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-red-400 focus:outline-none"
            />
          </div>

          {error && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-bold transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleLogin}
              className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg font-bold transition-colors"
            >
              Login
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Question Management Modal Component
const QuestionManagementModal = ({ isOpen, onClose }) => {
  const [selectedMode, setSelectedMode] = useState('SYNONYM');
  const [questions, setQuestions] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [newQuestion, setNewQuestion] = useState({});

  useEffect(() => {
    if (isOpen) {
      loadQuestions();
    }
  }, [isOpen, selectedMode]);

  const loadQuestions = () => {
    const modeQuestions = questionBankManager.getQuestionsByMode(selectedMode);
    setQuestions(modeQuestions);
  };

  const handleAddQuestion = () => {
    if (validateQuestion(newQuestion)) {
      questionBankManager.addQuestion(selectedMode, newQuestion);
      loadQuestions();
      setNewQuestion({});
      setShowAddForm(false);
    }
  };

  const handleUpdateQuestion = () => {
    if (validateQuestion(newQuestion) && editingQuestion) {
      questionBankManager.updateQuestion(selectedMode, editingQuestion.id, newQuestion);
      loadQuestions();
      setNewQuestion({});
      setEditingQuestion(null);
    }
  };

  const handleDeleteQuestion = (questionId) => {
    if (confirm('Are you sure you want to delete this question?')) {
      questionBankManager.deleteQuestion(selectedMode, questionId);
      loadQuestions();
    }
  };

  const validateQuestion = (question) => {
    const headers = CONSTANTS.QUESTION_BANK.EXCEL_HEADERS[selectedMode];
    return headers.every(header => question[header] && question[header].toString().trim().length > 0);
  };

  const handleExcelUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        // Simple CSV parsing (assuming Excel is saved as CSV)
        const text = e.target.result;
        const lines = text.split('\n');
        const headers = CONSTANTS.QUESTION_BANK.EXCEL_HEADERS[selectedMode];
        
        const importedQuestions = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
          if (values.length >= headers.length) {
            const question = {};
            headers.forEach((header, index) => {
              if (header === 'correctAnswers' && selectedMode === 'SYNONYM') {
                question[header] = values[index].split(';').map(v => v.trim());
              } else {
                question[header] = values[index];
              }
            });
            importedQuestions.push(question);
          }
        }

        const count = questionBankManager.importQuestionsFromExcel(selectedMode, importedQuestions);
        alert(`Successfully imported ${count} questions!`);
        loadQuestions();
      } catch (error) {
        alert('Error parsing Excel file. Please check the format.');
      }
    };
    reader.readAsText(file);
  };

  const exportTemplate = () => {
    const headers = CONSTANTS.QUESTION_BANK.EXCEL_HEADERS[selectedMode];
    const csvContent = headers.join(',') + '\n';
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedMode}_template.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const renderQuestionForm = () => {
    const headers = CONSTANTS.QUESTION_BANK.EXCEL_HEADERS[selectedMode];
    
    return (
      <div className="space-y-4">
        {headers.map(header => (
          <div key={header}>
            <label className="block text-white font-medium mb-2 capitalize">
              {header.replace(/([A-Z])/g, ' $1').trim()}
            </label>
            {header === 'correctAnswers' && selectedMode === 'SYNONYM' ? (
              <input
                type="text"
                value={Array.isArray(newQuestion[header]) ? newQuestion[header].join(', ') : newQuestion[header] || ''}
                onChange={(e) => setNewQuestion({
                  ...newQuestion,
                  [header]: e.target.value.split(',').map(v => v.trim())
                })}
                placeholder="Enter synonyms separated by commas"
                className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-cyan-400 focus:outline-none"
              />
            ) : header === 'level' ? (
              <select
                value={newQuestion[header] || ''}
                onChange={(e) => setNewQuestion({ ...newQuestion, [header]: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-cyan-400 focus:outline-none"
              >
                <option value="">Select Level</option>
                {CONSTANTS.CEFR_LEVELS.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            ) : header === 'from' || header === 'to' ? (
              <select
                value={newQuestion[header] || ''}
                onChange={(e) => setNewQuestion({ ...newQuestion, [header]: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-cyan-400 focus:outline-none"
              >
                <option value="">Select Part of Speech</option>
                {CONSTANTS.PARTS_OF_SPEECH.filter(pos => pos !== 'ANY').map(pos => (
                  <option key={pos} value={pos}>{pos}</option>
                ))}
              </select>
            ) : header === 'rule' ? (
              <select
                value={newQuestion[header] || ''}
                onChange={(e) => setNewQuestion({ ...newQuestion, [header]: e.target.value })}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-cyan-400 focus:outline-none"
              >
                <option value="">Select Grammar Rule</option>
                {Object.entries(CONSTANTS.GRAMMAR_RULES).map(([key, value]) => (
                  <option key={key} value={key}>{value}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={newQuestion[header] || ''}
                onChange={(e) => setNewQuestion({ ...newQuestion, [header]: e.target.value })}
                placeholder={`Enter ${header}`}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-cyan-400 focus:outline-none"
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gray-800 rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <h3 className="text-2xl font-bold text-white">Question Management</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[80vh]">
          {/* Mode Selection */}
          <div className="mb-6">
            <label className="block text-white font-medium mb-2">Game Mode</label>
            <select
              value={selectedMode}
              onChange={(e) => setSelectedMode(e.target.value)}
              className="px-4 py-2 bg-gray-700 text-white rounded border border-gray-600 focus:border-cyan-400 focus:outline-none"
            >
              {CONSTANTS.GAME_MODES.filter(mode => mode !== 'LEVEL_UP').map(mode => (
                <option key={mode} value={mode}>{mode.replace('_', ' ')}</option>
              ))}
            </select>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mb-6">
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded"
            >
              <Plus size={16} />
              Add Question
            </button>
            <button
              onClick={exportTemplate}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded"
            >
              <Download size={16} />
              Download Template
            </button>
            <label className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded cursor-pointer">
              <Upload size={16} />
              Upload Excel
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleExcelUpload}
                className="hidden"
              />
            </label>
          </div>

          {/* Add/Edit Form */}
          {(showAddForm || editingQuestion) && (
            <div className="bg-gray-700 rounded-lg p-4 mb-6">
              <h4 className="text-lg font-bold text-white mb-4">
                {editingQuestion ? 'Edit Question' : 'Add New Question'}
              </h4>
              {renderQuestionForm()}
              <div className="flex gap-3 mt-4">
                <button
                  onClick={editingQuestion ? handleUpdateQuestion : handleAddQuestion}
                  className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded"
                >
                  <Save size={16} className="inline mr-2" />
                  {editingQuestion ? 'Update' : 'Add'}
                </button>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingQuestion(null);
                    setNewQuestion({});
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Questions List */}
          <div className="space-y-3">
            <h4 className="text-lg font-bold text-white">
              Existing Questions ({questions.length})
            </h4>
            {questions.map((question, index) => (
              <div key={question.id || index} className="bg-gray-700 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    {selectedMode === 'SYNONYM' && (
                      <div>
                        <div className="text-white font-medium">Word: {question.word}</div>
                        <div className="text-gray-300 text-sm">Answers: {Array.isArray(question.correctAnswers) ? question.correctAnswers.join(', ') : question.correctAnswers}</div>
                        <div className="text-gray-400 text-sm">Level: {question.level}</div>
                      </div>
                    )}
                    {selectedMode === 'GRAMMAR' && (
                      <div>
                        <div className="text-white font-medium">Sentence: {question.sentence}</div>
                        <div className="text-gray-300 text-sm">Correct: {question.correctAnswer}</div>
                        <div className="text-gray-400 text-sm">Rule: {CONSTANTS.GRAMMAR_RULES[question.rule]} | Level: {question.level}</div>
                      </div>
                    )}
                    {selectedMode === 'PASSIVE_ACTIVE' && (
                      <div>
                        <div className="text-white font-medium">Sentence: {question.sentence}</div>
                        <div className="text-gray-300 text-sm">Answer: {question.correctAnswer}</div>
                        <div className="text-gray-400 text-sm">Level: {question.level}</div>
                      </div>
                    )}
                    {selectedMode === 'PART_OF_SPEECH' && (
                      <div>
                        <div className="text-white font-medium">Word: {question.word}</div>
                        <div className="text-gray-300 text-sm">Convert {question.from} to {question.to}: {question.correctAnswer}</div>
                        <div className="text-gray-400 text-sm">Level: {question.level}</div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingQuestion(question);
                        setNewQuestion(question);
                        setShowAddForm(false);
                      }}
                      className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteQuestion(question.id)}
                      className="p-2 bg-red-500 hover:bg-red-600 text-white rounded"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Level-Up Celebration Effect Component
const LevelUpEffect = ({ newLevel, onComplete }) => {
  const [isVisible, setIsVisible] = useState(true);
  const levelColors = CONSTANTS.LEVEL_UP_MODE.LEVEL_COLORS[newLevel];
  const config = CONSTANTS.LEVEL_UP_MODE.CELEBRATION_EFFECTS;

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      onComplete();
    }, config.ANIMATION_DURATION);
    return () => clearTimeout(timer);
  }, [onComplete, config.ANIMATION_DURATION]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center">
      {/* Screen shake and background flash */}
      <motion.div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, ${levelColors.from}, ${levelColors.to})`,
        }}
        initial={{ opacity: 0 }}
        animate={{ 
          opacity: [0, 0.3, 0],
        }}
        transition={{ 
          duration: 1,
          times: [0, 0.3, 1],
          ease: 'easeOut'
        }}
      />

      {/* Expanding rings */}
      <div className="absolute inset-0 flex items-center justify-center">
        {[...Array(config.RING_COUNT)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute border-4 rounded-full"
            style={{ borderColor: levelColors.accent }}
            initial={{ scale: 0, opacity: 1 }}
            animate={{ 
              scale: 3 + i * 0.5,
              opacity: 0 
            }}
            transition={{ 
              duration: 2,
              delay: i * 0.2,
              ease: 'easeOut'
            }}
          />
        ))}
      </div>

      {/* Particle burst */}
      <div className="absolute inset-0 flex items-center justify-center">
        {[...Array(config.PARTICLE_COUNT)].map((_, i) => {
          const angle = (i / config.PARTICLE_COUNT) * 2 * Math.PI;
          const distance = 150 + Math.random() * 300;
          const x = Math.cos(angle) * distance;
          const y = Math.sin(angle) * distance;
          
          return (
            <motion.div
              key={i}
              className="absolute rounded-full"
              style={{
                width: 8,
                height: 8,
                backgroundColor: levelColors.accent,
                boxShadow: `0 0 16px ${levelColors.accent}`
              }}
              initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
              animate={{ 
                x: x,
                y: y,
                scale: [0, 1, 0],
                opacity: [1, 1, 0]
              }}
              transition={{ 
                duration: 2,
                ease: 'easeOut'
              }}
            />
          );
        })}
      </div>

      {/* Main level-up text */}
      <motion.div
        className="text-center font-bold relative z-10"
        initial={{ scale: 0, opacity: 0, y: 50 }}
        animate={{ 
          scale: [0, 1.3, 1],
          opacity: [0, 1, 1, 0],
          y: [50, -20, -30, -50]
        }}
        transition={{ 
          duration: config.ANIMATION_DURATION / 1000,
          times: [0, 0.3, 0.7, 1],
          ease: 'easeOut'
        }}
      >
        <div 
          className="text-6xl font-orbitron mb-4"
          style={{ 
            color: levelColors.accent,
            textShadow: `0 0 30px ${levelColors.accent}, 0 0 60px ${levelColors.accent}`,
          }}
        >
          LEVEL UP!
        </div>
        <div 
          className="text-4xl font-orbitron"
          style={{ 
            color: levelColors.accent,
            textShadow: `0 0 20px ${levelColors.accent}`,
          }}
        >
          {newLevel} - {CONSTANTS.CEFR_DESCRIPTIONS[newLevel]}
        </div>
      </motion.div>
    </div>
  );
};

// Combo Effect Component
const ComboEffect = ({ combo, onComplete }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [screenShake, setScreenShake] = useState(false);
  
  const effectConfig = useMemo(() => {
    if (combo >= 10) return CONSTANTS.COMBO_EFFECTS.TEXT_EFFECTS[10];
    if (combo >= 7) return CONSTANTS.COMBO_EFFECTS.TEXT_EFFECTS[7];
    if (combo >= 5) return CONSTANTS.COMBO_EFFECTS.TEXT_EFFECTS[5];
    if (combo >= 3) return CONSTANTS.COMBO_EFFECTS.TEXT_EFFECTS[3];
    return null;
  }, [combo]);

  const particleConfig = useMemo(() => {
    if (combo >= 10) return CONSTANTS.COMBO_EFFECTS.PARTICLE_BURST[10];
    if (combo >= 7) return CONSTANTS.COMBO_EFFECTS.PARTICLE_BURST[7];
    if (combo >= 5) return CONSTANTS.COMBO_EFFECTS.PARTICLE_BURST[5];
    if (combo >= 3) return CONSTANTS.COMBO_EFFECTS.PARTICLE_BURST[3];
    return null;
  }, [combo]);

  const ringConfig = useMemo(() => {
    if (combo >= 10) return CONSTANTS.COMBO_EFFECTS.RING_EFFECTS[10];
    if (combo >= 7) return CONSTANTS.COMBO_EFFECTS.RING_EFFECTS[7];
    if (combo >= 5) return CONSTANTS.COMBO_EFFECTS.RING_EFFECTS[5];
    if (combo >= 3) return CONSTANTS.COMBO_EFFECTS.RING_EFFECTS[3];
    return null;
  }, [combo]);

  const shakeConfig = useMemo(() => {
    if (combo >= 10) return CONSTANTS.COMBO_EFFECTS.SCREEN_SHAKE[10];
    if (combo >= 7) return CONSTANTS.COMBO_EFFECTS.SCREEN_SHAKE[7];
    if (combo >= 5) return CONSTANTS.COMBO_EFFECTS.SCREEN_SHAKE[5];
    if (combo >= 3) return CONSTANTS.COMBO_EFFECTS.SCREEN_SHAKE[3];
    return null;
  }, [combo]);

  useEffect(() => {
    if (shakeConfig) {
      setScreenShake(true);
      const timer = setTimeout(() => {
        setScreenShake(false);
      }, shakeConfig.duration);
      return () => clearTimeout(timer);
    }
  }, [shakeConfig]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      onComplete();
    }, 2000);
    return () => clearTimeout(timer);
  }, [onComplete]);

  if (!effectConfig || !isVisible) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center">
      {/* Screen shake container */}
      <motion.div
        className="relative"
        animate={screenShake ? {
          x: [-shakeConfig.intensity, shakeConfig.intensity, -shakeConfig.intensity, shakeConfig.intensity, 0],
          y: [-shakeConfig.intensity/2, shakeConfig.intensity/2, -shakeConfig.intensity/2, shakeConfig.intensity/2, 0]
        } : {}}
        transition={{ duration: 0.1, repeat: screenShake ? 3 : 0 }}
      >
        {/* Expanding rings */}
        {ringConfig && (
          <div className="absolute inset-0 flex items-center justify-center">
            {[...Array(ringConfig.rings)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute border-4 rounded-full"
                style={{ borderColor: ringConfig.color }}
                initial={{ scale: 0, opacity: 1 }}
                animate={{ 
                  scale: ringConfig.maxRadius / 50,
                  opacity: 0 
                }}
                transition={{ 
                  duration: 1.5,
                  delay: i * 0.2,
                  ease: 'easeOut'
                }}
              />
            ))}
          </div>
        )}

        {/* Particle burst */}
        {particleConfig && (
          <div className="absolute inset-0 flex items-center justify-center">
            {[...Array(particleConfig.count)].map((_, i) => {
              const angle = (i / particleConfig.count) * 2 * Math.PI;
              const distance = 100 + Math.random() * 200;
              const x = Math.cos(angle) * distance;
              const y = Math.sin(angle) * distance;
              const color = particleConfig.colors[i % particleConfig.colors.length];
              
              return (
                <motion.div
                  key={i}
                  className="absolute rounded-full"
                  style={{
                    width: particleConfig.size,
                    height: particleConfig.size,
                    backgroundColor: color,
                    boxShadow: `0 0 ${particleConfig.size * 2}px ${color}`
                  }}
                  initial={{ x: 0, y: 0, scale: 0, opacity: 1 }}
                  animate={{ 
                    x: x,
                    y: y,
                    scale: [0, 1, 0],
                    opacity: [1, 1, 0]
                  }}
                  transition={{ 
                    duration: 1.5,
                    ease: 'easeOut'
                  }}
                />
              );
            })}
          </div>
        )}

        {/* Main text effect */}
        <motion.div
          className="text-center font-bold"
          style={{ 
            color: effectConfig.color,
            fontSize: `${effectConfig.scale * 2}rem`,
            textShadow: `0 0 20px ${effectConfig.color}, 0 0 40px ${effectConfig.color}`,
            fontFamily: 'Orbitron, sans-serif'
          }}
          initial={{ scale: 0, opacity: 0, y: 50 }}
          animate={{ 
            scale: [0, effectConfig.scale * 1.2, effectConfig.scale],
            opacity: [0, 1, 1, 0],
            y: [50, -20, -30, -50]
          }}
          transition={{ 
            duration: 2,
            times: [0, 0.3, 0.7, 1],
            ease: 'easeOut'
          }}
        >
          {effectConfig.text}
          <motion.div
            className="text-lg mt-2"
            style={{ color: effectConfig.color }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 1, 0] }}
            transition={{ duration: 2, times: [0, 0.3, 0.7, 1] }}
          >
            {combo}x COMBO!
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
};

const MilitaryBackground = () => {
  const config = CONSTANTS.THEME_CONFIGS.MILITARY;
  
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Base gradient */}
      <div 
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, ${config.gradientFrom}, ${config.gradientTo})`,
        }}
      />
      
      {/* Tactical grid overlay */}
      <div className="absolute inset-0 opacity-20">
        <motion.div
          className="w-full h-full"
          style={{
            backgroundImage: `
              linear-gradient(90deg, ${config.accentColor}40 1px, transparent 1px),
              linear-gradient(${config.accentColor}40 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
          }}
          animate={{
            backgroundPosition: ['0px 0px', '40px 40px'],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
      </div>

      {/* Moving radar sweep */}
      <div className="absolute inset-0 opacity-30">
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{
              background: `conic-gradient(from 0deg, transparent, ${config.primaryColor}40, transparent)`,
              borderRadius: '50%',
              width: '200px',
              height: '200px',
              left: `${20 + i * 30}%`,
              top: `${30 + i * 20}%`,
            }}
            animate={{
              rotate: [0, 360],
            }}
            transition={{
              duration: 6 + i * 2,
              repeat: Infinity,
              ease: 'linear',
            }}
          />
        ))}
      </div>

      {/* Camouflage pattern overlay */}
      <div className="absolute inset-0 opacity-10">
        <div 
          className="w-full h-full"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='${config.primaryColor.replace('#', '%23')}' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>
    </div>
  );
};

const EducationBackground = () => {
  const config = CONSTANTS.THEME_CONFIGS.EDUCATION;
  
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Base gradient */}
      <div 
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, ${config.gradientFrom}, ${config.gradientTo})`,
        }}
      />
      
      {/* Floating academic elements */}
      <div className="absolute inset-0 opacity-20">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-4xl"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              color: config.accentColor,
            }}
            animate={{
              y: [-20, 20, -20],
              rotate: [-5, 5, -5],
              opacity: [0.3, 0.7, 0.3],
            }}
            transition={{
              duration: 4 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          >
            {['📚', '✏️', '🎓', '📝', '🔬', '📐', '🧮'][i % 7]}
          </motion.div>
        ))}
      </div>

      {/* Notebook lines pattern */}
      <div className="absolute inset-0 opacity-10">
        <div 
          className="w-full h-full"
          style={{
            backgroundImage: `repeating-linear-gradient(
              0deg,
              transparent,
              transparent 29px,
              ${config.primaryColor}40 30px
            )`,
          }}
        />
      </div>

      {/* Gentle wave motion */}
      <div className="absolute bottom-0 left-0 right-0 h-1/3 opacity-30">
        <motion.div
          className="w-full h-full"
          style={{
            background: `linear-gradient(45deg, ${config.primaryColor}20, ${config.accentColor}20)`,
            borderRadius: '50% 50% 0 0',
          }}
          animate={{
            scaleX: [1, 1.1, 1],
            scaleY: [1, 0.9, 1],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>
    </div>
  );
};

const FantasyBackground = () => {
  const config = CONSTANTS.THEME_CONFIGS.FANTASY;
  
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Base gradient */}
      <div 
        className="absolute inset-0"
        style={{
          background: `linear-gradient(135deg, ${config.gradientFrom}, ${config.gradientTo})`,
        }}
      />
      
      {/* Magical particles */}
      <div className="absolute inset-0">
        {[...Array(25)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 rounded-full"
            style={{
              background: config.accentColor,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              boxShadow: `0 0 10px ${config.accentColor}`,
            }}
            animate={{
              y: [-30, 30, -30],
              x: [-15, 15, -15],
              opacity: [0, 1, 0],
              scale: [0.5, 1.5, 0.5],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 3,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>

      {/* Mystical energy waves */}
      <div className="absolute inset-0 opacity-20">
        {[...Array(4)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full border-2"
            style={{
              borderColor: config.primaryColor,
              width: `${200 + i * 100}px`,
              height: `${200 + i * 100}px`,
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
            }}
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.5, 0, 0.5],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              delay: i * 1,
            }}
          />
        ))}
      </div>

      {/* Floating runes */}
      <div className="absolute inset-0 opacity-30">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-2xl font-bold"
            style={{
              left: `${10 + (i * 12)}%`,
              top: `${20 + Math.sin(i) * 30}%`,
              color: config.accentColor,
              textShadow: `0 0 10px ${config.accentColor}`,
            }}
            animate={{
              rotate: [0, 360],
              y: [-10, 10, -10],
            }}
            transition={{
              duration: 8 + i,
              repeat: Infinity,
              ease: 'linear',
            }}
          >
            {['ᚠ', 'ᚢ', 'ᚦ', 'ᚨ', 'ᚱ', 'ᚲ', 'ᚷ', 'ᚹ'][i]}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const LevelUpBackground = ({ currentLevel }) => {
  const levelColors = CONSTANTS.LEVEL_UP_MODE.LEVEL_COLORS[currentLevel];
  
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Dynamic gradient based on current level */}
      <motion.div 
        className="absolute inset-0"
        animate={{
          background: `linear-gradient(135deg, ${levelColors.from}, ${levelColors.to})`,
        }}
        transition={{ duration: 1 }}
      />
      
      {/* Level-specific animated elements */}
      <div className="absolute inset-0 opacity-20">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-3 h-3 rounded-full"
            style={{
              background: levelColors.accent,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              boxShadow: `0 0 15px ${levelColors.accent}`,
            }}
            animate={{
              y: [-40, 40, -40],
              x: [-20, 20, -20],
              opacity: [0, 1, 0],
              scale: [0.5, 1.5, 0.5],
            }}
            transition={{
              duration: 4 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 3,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>

      {/* Pulsing energy rings */}
      <div className="absolute inset-0 opacity-30">
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full border-2"
            style={{
              borderColor: levelColors.accent,
              width: `${300 + i * 150}px`,
              height: `${300 + i * 150}px`,
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
            }}
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.7, 0.3],
            }}
            transition={{
              duration: 3 + i,
              repeat: Infinity,
              delay: i * 0.5,
            }}
          />
        ))}
      </div>
    </div>
  );
};

const ThemeBackground = ({ theme, gameMode, currentLevel }) => {
  if (gameMode === 'LEVEL_UP' && currentLevel) {
    return <LevelUpBackground currentLevel={currentLevel} />;
  }

  if (!theme) {
    return (
      <div className="absolute inset-0 overflow-hidden">
        {/* Default moving background */}
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-cyan-900/50 to-transparent"
          animate={{
            backgroundPosition: ['0% 50%', '100% 50%'],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: 'reverse',
            ease: 'linear',
          }}
          style={{
            backgroundImage: 'linear-gradient(90deg, #0f172a, #1e3a8a, #0f172a)',
            backgroundSize: '200% 100%',
            filter: 'blur(20px)',
            opacity: 0.3,
          }}
        />
        <div className="absolute inset-0 opacity-20">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute bg-gradient-to-r from-transparent via-cyan-500 to-transparent h-2"
              style={{
                left: '-100%',
                top: `${i * 5}%`,
                width: '200%',
                transform: 'skewX(-45deg)',
              }}
              animate={{
                x: ['0%', '100%'],
              }}
              transition={{
                duration: 2 + (i % 3),
                repeat: Infinity,
                ease: 'linear',
                delay: i * 0.1,
              }}
            />
          ))}
        </div>
        <div className="absolute inset-0 opacity-10">
          <motion.div
            className="w-full h-full"
            style={{
              backgroundImage: `
                linear-gradient(90deg, rgba(6, 182, 212, 0.3) 1px, transparent 1px),
                linear-gradient(rgba(6, 182, 212, 0.3) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px',
              transform: 'perspective(500px) rotateX(60deg)',
              transformOrigin: 'center bottom',
            }}
            animate={{
              backgroundPosition: ['0px 0px', '50px 50px'],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'linear',
            }}
          />
        </div>
      </div>
    );
  }

  switch (theme) {
    case 'MILITARY':
      return <MilitaryBackground />;
    case 'EDUCATION':
      return <EducationBackground />;
    case 'FANTASY':
      return <FantasyBackground />;
    default:
      return <MilitaryBackground />;
  }
};

const Footer = () => {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4">
      <div className="text-center">
        <p className="text-gray-400 text-sm">
          Co-developed by <span className="text-cyan-400 font-semibold">FAHIM</span> 2025 with love ❤️
        </p>
      </div>
    </div>
  );
};

const AnimatedTitle = () => {
  const titleText = "CEFR";
  const subtitleText = "calypse";
  
  return (
    <div className="relative">
      {/* Background glow effect */}
      <motion.div
        className="absolute inset-0 blur-3xl opacity-30"
        animate={{
          background: [
            `radial-gradient(circle, ${CONSTANTS.TITLE_ANIMATION.GLOW_COLORS[0]}40, transparent)`,
            `radial-gradient(circle, ${CONSTANTS.TITLE_ANIMATION.GLOW_COLORS[1]}40, transparent)`,
            `radial-gradient(circle, ${CONSTANTS.TITLE_ANIMATION.GLOW_COLORS[2]}40, transparent)`,
            `radial-gradient(circle, ${CONSTANTS.TITLE_ANIMATION.GLOW_COLORS[3]}40, transparent)`,
            `radial-gradient(circle, ${CONSTANTS.TITLE_ANIMATION.GLOW_COLORS[0]}40, transparent)`,
          ],
        }}
        transition={{
          duration: CONSTANTS.TITLE_ANIMATION.PULSE_DURATION * 2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Floating particles around title */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(CONSTANTS.TITLE_ANIMATION.PARTICLE_COUNT)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              boxShadow: '0 0 6px currentColor',
            }}
            animate={{
              y: [-20, 20, -20],
              x: [-10, 10, -10],
              opacity: [0, 1, 0],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: CONSTANTS.TITLE_ANIMATION.FLOAT_DURATION,
              repeat: Infinity,
              delay: i * 0.2,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>

      {/* Main title with staggered animation */}
      <motion.div
        className="relative z-10"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: 'easeOut' }}
      >
        <div className="text-center">
          {/* CEFR part */}
          <div className="inline-block">
            {titleText.split('').map((letter, index) => (
              <motion.span
                key={index}
                className="inline-block text-8xl md:text-9xl font-bold font-orbitron"
                style={{
                  background: `linear-gradient(45deg, #ffffff, #06b6d4, #3b82f6)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  textShadow: '0 0 30px rgba(6, 182, 212, 0.5)',
                }}
                initial={{ opacity: 0, y: 50, rotateX: -90 }}
                animate={{ 
                  opacity: 1, 
                  y: 0, 
                  rotateX: 0,
                  scale: CONSTANTS.TITLE_ANIMATION.SCALE_RANGE,
                }}
                transition={{
                  duration: 0.8,
                  delay: index * CONSTANTS.TITLE_ANIMATION.STAGGER_DELAY,
                  ease: 'easeOut',
                  scale: {
                    duration: CONSTANTS.TITLE_ANIMATION.PULSE_DURATION,
                    repeat: Infinity,
                    repeatType: 'reverse',
                    ease: 'easeInOut',
                  }
                }}
                whileHover={{
                  scale: 1.1,
                  textShadow: '0 0 40px rgba(6, 182, 212, 0.8)',
                }}
              >
                {letter}
              </motion.span>
            ))}
          </div>

          {/* calypse part */}
          <motion.div
            className="inline-block"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: 'easeOut' }}
          >
            {subtitleText.split('').map((letter, index) => (
              <motion.span
                key={index}
                className="inline-block text-8xl md:text-9xl font-bold font-orbitron"
                style={{
                  background: `linear-gradient(45deg, #06b6d4, #8b5cf6, #10b981)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  textShadow: '0 0 30px rgba(139, 92, 246, 0.5)',
                }}
                animate={{
                  scale: CONSTANTS.TITLE_ANIMATION.SCALE_RANGE,
                  textShadow: [
                    '0 0 30px rgba(139, 92, 246, 0.5)',
                    '0 0 40px rgba(139, 92, 246, 0.8)',
                    '0 0 30px rgba(139, 92, 246, 0.5)',
                  ],
                }}
                transition={{
                  scale: {
                    duration: CONSTANTS.TITLE_ANIMATION.PULSE_DURATION,
                    repeat: Infinity,
                    repeatType: 'reverse',
                    ease: 'easeInOut',
                    delay: index * 0.05,
                  },
                  textShadow: {
                    duration: CONSTANTS.TITLE_ANIMATION.PULSE_DURATION * 1.5,
                    repeat: Infinity,
                    repeatType: 'reverse',
                    ease: 'easeInOut',
                    delay: index * 0.1,
                  }
                }}
                whileHover={{
                  scale: 1.1,
                  textShadow: '0 0 50px rgba(139, 92, 246, 1)',
                }}
              >
                {letter}
              </motion.span>
            ))}
          </motion.div>
        </div>

        {/* Subtitle */}
        <motion.p
          className="text-xl md:text-2xl text-gray-300 mt-6 font-medium"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          style={{
            textShadow: '0 0 20px rgba(255, 255, 255, 0.3)',
          }}
        >
          Choose Your Battle Theme
        </motion.p>
      </motion.div>

      {/* Energy rings around title */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute border border-cyan-400/20 rounded-full"
            style={{
              width: `${300 + i * 100}px`,
              height: `${300 + i * 100}px`,
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
            }}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.2, 0.5, 0.2],
              rotate: [0, 360],
            }}
            transition={{
              duration: 4 + i,
              repeat: Infinity,
              ease: 'linear',
            }}
          />
        ))}
      </div>
    </div>
  );
};

const PlayerInfoModal = ({ isOpen, onComplete }) => {
  const [name, setName] = useState('');
  const [className, setClassName] = useState('');
  const [errors, setErrors] = useState([]);
  const [showAdminLogin, setShowAdminLogin] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const savedInfo = playerInfoManager.getPlayerInfo();
      setName(savedInfo.name);
      setClassName(savedInfo.class);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    const validationErrors = playerInfoManager.validatePlayerInfo(name, className);
    
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    const playerInfo = {
      name: name.trim(),
      class: className.trim()
    };

    playerInfoManager.savePlayerInfo(playerInfo);
    onComplete(playerInfo);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const handleAdminLoginSuccess = () => {
    // Skip player info for admin
    onComplete({ name: 'Super Admin', class: 'Lecturer', isAdmin: true });
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gray-800 rounded-xl max-w-md w-full p-6"
        >
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-cyan-500/20 rounded-full mx-auto mb-4 flex items-center justify-center">
              <User size={32} className="text-cyan-400" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Welcome, Student!</h3>
            <p className="text-gray-400">Please enter your details to get started</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-white font-medium mb-2">
                <User size={16} className="inline mr-2" />
                Your Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter your full name"
                className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-cyan-400 focus:outline-none"
                maxLength={CONSTANTS.PLAYER_INFO.MAX_NAME_LENGTH}
              />
            </div>

            <div>
              <label className="block text-white font-medium mb-2">
                <School size={16} className="inline mr-2" />
                Your Class
              </label>
              <input
                type="text"
                value={className}
                onChange={(e) => setClassName(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="e.g., 5A, Form 4 Science, Year 12"
                className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-cyan-400 focus:outline-none"
                maxLength={CONSTANTS.PLAYER_INFO.MAX_CLASS_LENGTH}
              />
            </div>

            {errors.length > 0 && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3">
                {errors.map((error, index) => (
                  <p key={index} className="text-red-400 text-sm">{error}</p>
                ))}
              </div>
            )}

            <button
              onClick={handleSubmit}
              className="w-full px-4 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-bold transition-colors"
            >
              Continue to Game
            </button>

            <div className="text-center">
              <button
                onClick={() => setShowAdminLogin(true)}
                className="text-red-400 hover:text-red-300 text-sm underline"
              >
                Login as Super Admin (Lecturer)
              </button>
            </div>
          </div>

          <div className="mt-4 text-center">
            <p className="text-gray-500 text-xs">
              Your information is stored locally on your device
            </p>
          </div>
        </motion.div>
      </div>

      <AdminLoginModal
        isOpen={showAdminLogin}
        onClose={() => setShowAdminLogin(false)}
        onLoginSuccess={handleAdminLoginSuccess}
      />
    </>
  );
};

const HowToPlayModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!isOpen) return null;

  const getIconComponent = (iconName) => {
    const icons = {
      Type,
      BookOpen,
      RefreshCw,
      Shuffle,
      TrendingUp
    };
    return icons[iconName] || Type;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <Info size={32} className="text-cyan-400" />
            <h3 className="text-3xl font-bold text-white">{CONSTANTS.HOW_TO_PLAY.TITLE}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X size={24} />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-700">
          {[
            { id: 'overview', label: 'Overview', icon: Info },
            { id: 'cefr', label: 'CEFR Levels', icon: GraduationCap },
            { id: 'modes', label: 'Game Modes', icon: Target },
            { id: 'features', label: 'Features', icon: Star }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-cyan-400 border-b-2 border-cyan-400 bg-gray-700/50'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <Icon size={20} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h4 className="text-xl font-bold text-white mb-3">Welcome to CEFRcalypse!</h4>
                <p className="text-gray-300 leading-relaxed">
                  {CONSTANTS.HOW_TO_PLAY.OVERVIEW}
                </p>
              </div>

              <div>
                <h4 className="text-lg font-bold text-white mb-3">How to Play:</h4>
                <ol className="list-decimal list-inside space-y-2 text-gray-300">
                  <li>Choose your battle theme (Military, Education, or Fantasy)</li>
                  <li>Select a game mode that interests you</li>
                  <li>Pick your CEFR level based on your English proficiency</li>
                  <li>Answer questions as quickly and accurately as possible</li>
                  <li>Build combos for bonus points and unlock power-ups</li>
                  <li>Use hints and skips strategically when needed</li>
                </ol>
              </div>

              <div>
                <h4 className="text-lg font-bold text-white mb-3">Pro Tips:</h4>
                <div className="space-y-2">
                  {CONSTANTS.HOW_TO_PLAY.TIPS.map((tip, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                      <p className="text-gray-300">{tip}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'cefr' && (
            <div className="space-y-6">
              <div>
                <h4 className="text-xl font-bold text-white mb-3">{CONSTANTS.HOW_TO_PLAY.CEFR_INFO.TITLE}</h4>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  {CONSTANTS.HOW_TO_PLAY.CEFR_INFO.DESCRIPTION}
                </p>
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                  <h5 className="text-cyan-400 font-bold mb-2">Why CEFR Matters in Malaysia:</h5>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    {CONSTANTS.HOW_TO_PLAY.CEFR_INFO.IMPORTANCE}
                  </p>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-bold text-white mb-4">CEFR Levels Explained:</h4>
                <div className="grid gap-4">
                  {CONSTANTS.HOW_TO_PLAY.CEFR_INFO.LEVELS.map((level, index) => (
                    <motion.div
                      key={level.level}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-gray-700 rounded-lg p-4 border-l-4 border-cyan-400"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="text-2xl font-bold text-cyan-400">{level.level}</div>
                        <div className="text-lg font-bold text-white">{level.name}</div>
                      </div>
                      <p className="text-gray-300 text-sm leading-relaxed">{level.description}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'modes' && (
            <div className="space-y-6">
              <div>
                <h4 className="text-xl font-bold text-white mb-4">Game Modes</h4>
                <p className="text-gray-300 mb-6">
                  Each game mode focuses on different aspects of English language learning:
                </p>
              </div>

              <div className="grid gap-6">
                {CONSTANTS.HOW_TO_PLAY.GAME_MODES.map((mode, index) => {
                  const Icon = getIconComponent(mode.icon);
                  return (
                    <motion.div
                      key={mode.name}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-gray-700 rounded-lg p-6"
                    >
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                          <Icon size={24} className="text-cyan-400" />
                        </div>
                        <div>
                          <h5 className="text-xl font-bold text-white">{mode.name}</h5>
                          <p className="text-gray-400">{mode.description}</p>
                        </div>
                      </div>
                      <p className="text-gray-300 mb-3 leading-relaxed">{mode.details}</p>
                      <div className="bg-gray-800 rounded-lg p-3">
                        <div className="text-sm text-gray-400 mb-1">Example:</div>
                        <div className="text-cyan-400 font-mono text-sm">{mode.example}</div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'features' && (
            <div className="space-y-6">
              <div>
                <h4 className="text-xl font-bold text-white mb-4">Gameplay Features</h4>
                <p className="text-gray-300 mb-6">
                  CEFRcalypse includes several features to enhance your learning experience:
                </p>
              </div>

              <div className="grid gap-4">
                {CONSTANTS.HOW_TO_PLAY.GAMEPLAY_FEATURES.map((feature, index) => (
                  <motion.div
                    key={feature.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-gray-700 rounded-lg p-4"
                  >
                    <h5 className="text-lg font-bold text-white mb-2">{feature.name}</h5>
                    <p className="text-gray-300 leading-relaxed">{feature.description}</p>
                  </motion.div>
                ))}
              </div>

              <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/30 rounded-lg p-6 mt-6">
                <h5 className="text-lg font-bold text-white mb-3">Scoring System</h5>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-cyan-400 font-medium">Base Points:</div>
                    <div className="text-gray-300">Synonym: 100 pts</div>
                    <div className="text-gray-300">Grammar: 150 pts</div>
                    <div className="text-gray-300">Passive/Active: 120 pts</div>
                    <div className="text-gray-300">Part of Speech: 110 pts</div>
                  </div>
                  <div>
                    <div className="text-purple-400 font-medium">Combo Bonuses:</div>
                    <div className="text-gray-300">3x combo: +50 pts</div>
                    <div className="text-gray-300">5x combo: +150 pts</div>
                    <div className="text-gray-300">10x combo: +400 pts</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-bold"
          >
            Got it!
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const Scoreboard = ({ isOpen, onClose, isAdmin }) => {
  const [scores, setScores] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setScores(scoreboardManager.getTopScores(20));
    }
  }, [isOpen]);

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getThemeColor = (theme) => {
    return CONSTANTS.SCOREBOARD.THEME_COLORS[theme] || '#6b7280';
  };

  const getModeColor = (mode) => {
    return CONSTANTS.SCOREBOARD.MODE_COLORS[mode] || '#6b7280';
  };

  const handleClearScores = () => {
    if (confirm('Are you sure you want to clear all scores? This action cannot be undone.')) {
      scoreboardManager.clearScores();
      setScores([]);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gray-800 rounded-xl p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden"
      >
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <Trophy size={32} className="text-yellow-400" />
            <h3 className="text-3xl font-bold text-white">Scoreboard</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X size={24} />
          </button>
        </div>

        {scores.length === 0 ? (
          <div className="text-center py-12">
            <Award size={64} className="text-gray-600 mx-auto mb-4" />
            <h4 className="text-xl text-gray-400 mb-2">No scores yet!</h4>
            <p className="text-gray-500">Play a game to see your scores here.</p>
          </div>
        ) : (
          <div className="overflow-y-auto max-h-96">
            <div className="space-y-3">
              {scores.map((score, index) => (
                <motion.div
                  key={score.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`p-4 rounded-lg bg-gray-700 border-l-4 ${
                    index === 0 ? 'border-yellow-400 bg-yellow-400/10' :
                    index === 1 ? 'border-gray-300 bg-gray-300/10' :
                    index === 2 ? 'border-orange-400 bg-orange-400/10' :
                    'border-gray-600'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-2xl font-bold text-white min-w-[3rem]">
                        #{index + 1}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-2xl font-bold text-cyan-400">
                            {score.score.toLocaleString()}
                          </span>
                          <span className="text-sm text-gray-400">points</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm mb-1">
                          <span 
                            className="px-2 py-1 rounded text-white font-medium"
                            style={{ backgroundColor: getThemeColor(score.theme) }}
                          >
                            {score.theme}
                          </span>
                          <span 
                            className="px-2 py-1 rounded text-white font-medium"
                            style={{ backgroundColor: getModeColor(score.gameMode) }}
                          >
                            {score.gameMode}
                          </span>
                          <span className="text-gray-400">
                            {score.cefrLevel}
                          </span>
                        </div>
                        {score.playerName && (
                          <div className="flex items-center gap-2 text-sm text-gray-300">
                            <User size={14} />
                            <span>{score.playerName}</span>
                            {score.playerClass && (
                              <>
                                <span className="text-gray-500">•</span>
                                <School size={14} />
                                <span>{score.playerClass}</span>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-gray-400 text-sm">
                        {formatDate(score.timestamp)}
                      </div>
                      {score.combo > 0 && (
                        <div className="text-purple-400 text-sm">
                          {score.combo}x combo
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-700">
          <div className="text-gray-400 text-sm">
            Showing top {Math.min(scores.length, 20)} scores
          </div>
          <div className="flex gap-3">
            {scores.length > 0 && isAdmin && (
              <button
                onClick={handleClearScores}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm"
              >
                Clear All Scores
              </button>
            )}
            {scores.length > 0 && !isAdmin && (
              <div className="px-4 py-2 bg-gray-600 text-gray-400 rounded-lg text-sm cursor-not-allowed">
                Clear All Scores (Admin Only)
              </div>
            )}
            <button
              onClick={onClose}
              className="px-6 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-bold"
            >
              Close
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const AudioSettings = ({ isOpen, onClose }) => {
  const [bgmVolume, setBgmVolume] = useState(audioManager.bgmVolume);
  const [sfxVolume, setSfxVolume] = useState(audioManager.sfxVolume);
  const [bgmMuted, setBgmMuted] = useState(audioManager.bgmMuted);
  const [sfxMuted, setSfxMuted] = useState(audioManager.sfxMuted);
  const [selectedTrack, setSelectedTrack] = useState(audioManager.currentTrack);

  const handleBGMVolumeChange = (volume) => {
    setBgmVolume(volume);
    audioManager.setBGMVolume(volume);
  };

  const handleSFXVolumeChange = (volume) => {
    setSfxVolume(volume);
    audioManager.setSFXVolume(volume);
  };

  const handleBGMMute = () => {
    audioManager.toggleBGMMute();
    setBgmMuted(audioManager.bgmMuted);
  };

  const handleSFXMute = () => {
    audioManager.toggleSFXMute();
    setSfxMuted(audioManager.sfxMuted);
  };

  const handleTrackSelect = (trackId) => {
    setSelectedTrack(trackId);
    audioManager.playBGM(trackId);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4"
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-2xl font-bold text-white">Audio Settings</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X size={24} />
          </button>
        </div>

        {/* BGM Selection */}
        <div className="mb-6">
          <h4 className="text-lg font-bold text-white mb-3">Background Music</h4>
          <div className="space-y-2">
            {CONSTANTS.AUDIO.BGM_TRACKS.map(track => (
              <div
                key={track.id}
                className={`p-3 rounded-lg cursor-pointer transition-all ${
                  selectedTrack === track.id
                    ? 'bg-cyan-500/20 border border-cyan-400'
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}
                onClick={() => handleTrackSelect(track.id)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium">{track.name}</div>
                    <div className="text-gray-400 text-sm">{track.description}</div>
                  </div>
                  {selectedTrack === track.id && (
                    <Check size={20} className="text-cyan-400" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* BGM Volume */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="text-white font-medium">BGM Volume</label>
            <button
              onClick={handleBGMMute}
              className={`p-2 rounded ${bgmMuted ? 'text-red-400' : 'text-cyan-400'}`}
            >
              {bgmMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>
          </div>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={bgmVolume}
            onChange={(e) => handleBGMVolumeChange(parseFloat(e.target.value))}
            className="w-full"
            disabled={bgmMuted}
          />
        </div>

        {/* SFX Volume */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="text-white font-medium">SFX Volume</label>
            <button
              onClick={handleSFXMute}
              className={`p-2 rounded ${sfxMuted ? 'text-red-400' : 'text-cyan-400'}`}
            >
              {sfxMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>
          </div>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={sfxVolume}
            onChange={(e) => handleSFXVolumeChange(parseFloat(e.target.value))}
            className="w-full"
            disabled={sfxMuted}
          />
        </div>

        <button
          onClick={onClose}
          className="w-full px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-bold"
        >
          Done
        </button>
      </motion.div>
    </div>
  );
};

// New StorylineModal Component
const StorylineModal = ({ isOpen, onClose, theme }) => {
  const [currentChapter, setCurrentChapter] = useState(0);
  const [showFullStory, setShowFullStory] = useState(false);
  
  const storyline = useMemo(() => {
    return theme ? CONSTANTS.THEME_STORYLINES[theme] : null;
  }, [theme]);

  useEffect(() => {
    if (isOpen && storyline) {
      // Play the recommended BGM for this theme's storyline
      audioManager.playBGM(storyline.recommendedBGM);
      setCurrentChapter(0);
      setShowFullStory(false);
    }
  }, [isOpen, storyline]);

  const handleNextChapter = () => {
    if (currentChapter < storyline.chapters.length) {
      setCurrentChapter(currentChapter + 1);
    } else {
      onClose();
    }
  };

  const handlePrevChapter = () => {
    if (currentChapter > 0) {
      setCurrentChapter(currentChapter - 1);
    }
  };

  const toggleFullStory = () => {
    setShowFullStory(!showFullStory);
  };

  if (!isOpen || !storyline) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-gray-800/90 backdrop-blur-sm rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header with theme-specific styling */}
        <div 
          className="p-6 border-b border-gray-700"
          style={{ 
            background: `linear-gradient(to right, ${CONSTANTS.THEME_CONFIGS[theme].gradientFrom}, ${CONSTANTS.THEME_CONFIGS[theme].gradientTo})` 
          }}
        >
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-1">{storyline.title}</h2>
              <p className="text-gray-200">{storyline.subtitle}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white bg-black/20 hover:bg-black/30 rounded-full p-2 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Story Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {showFullStory ? (
            <div className="space-y-6">
              {/* Introduction */}
              <div>
                <h3 className="text-xl font-bold text-white mb-3">Introduction</h3>
                <p className="text-gray-300 leading-relaxed">{storyline.introduction}</p>
              </div>
              
              {/* Mission */}
              <div>
                <h3 className="text-xl font-bold text-white mb-3">Your Mission</h3>
                <p className="text-gray-300 leading-relaxed">{storyline.mission}</p>
              </div>
              
              {/* All Chapters */}
              <div>
                <h3 className="text-xl font-bold text-white mb-3">Chapters</h3>
                <div className="space-y-4">
                  {storyline.chapters.map((chapter, index) => (
                    <div key={index} className="bg-gray-700/50 rounded-lg p-4">
                      <h4 className="text-lg font-bold text-white mb-2">{chapter.title}</h4>
                      <p className="text-gray-300">{chapter.content}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Conclusion */}
              <div>
                <h3 className="text-xl font-bold text-white mb-3">Conclusion</h3>
                <p className="text-gray-300 leading-relaxed">{storyline.conclusion}</p>
              </div>
            </div>
          ) : (
            <div>
              {currentChapter === 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-6"
                >
                  <div>
                    <h3 className="text-xl font-bold text-white mb-3">Introduction</h3>
                    <p className="text-gray-300 leading-relaxed">{storyline.introduction}</p>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-3">Your Mission</h3>
                    <p className="text-gray-300 leading-relaxed">{storyline.mission}</p>
                  </div>
                </motion.div>
              )}
              
              {currentChapter > 0 && currentChapter <= storyline.chapters.length && (
                <motion.div
                  key={`chapter-${currentChapter}`}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-6"
                >
                  <div>
                    <h3 className="text-xl font-bold text-white mb-3">
                      Chapter {currentChapter}: {storyline.chapters[currentChapter-1].title}
                    </h3>
                    <p className="text-gray-300 leading-relaxed">
                      {storyline.chapters[currentChapter-1].content}
                    </p>
                  </div>
                </motion.div>
              )}
              
              {currentChapter > storyline.chapters.length && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="space-y-6"
                >
                  <div>
                    <h3 className="text-xl font-bold text-white mb-3">Conclusion</h3>
                    <p className="text-gray-300 leading-relaxed">{storyline.conclusion}</p>
                  </div>
                </motion.div>
              )}
            </div>
          )}
        </div>

        {/* Footer with navigation */}
        <div className="flex justify-between items-center p-6 border-t border-gray-700">
          <div className="flex gap-3">
            <button
              onClick={toggleFullStory}
              className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
            >
              <Book size={18} />
              {showFullStory ? "Chapter View" : "Full Story"}
            </button>
            
            {!showFullStory && (
              <button
                onClick={handlePrevChapter}
                disabled={currentChapter === 0}
                className={`px-4 py-2 rounded-lg ${
                  currentChapter === 0 
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                    : 'bg-gray-700 hover:bg-gray-600 text-white'
                }`}
              >
                Previous
              </button>
            )}
          </div>
          
          <button
            onClick={showFullStory ? onClose : handleNextChapter}
            className="px-6 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-bold"
          >
            {showFullStory ? "Start Game" : 
             currentChapter > storyline.chapters.length ? "Start Game" : "Continue"}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const ThemeSelector = ({ selectedTheme, onThemeSelect, onStart, isAdmin, onLogout }) => {
  const [showAudioSettings, setShowAudioSettings] = useState(false);
  const [showScoreboard, setShowScoreboard] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [showQuestionManagement, setShowQuestionManagement] = useState(false);
  const [showStoryline, setShowStoryline] = useState(false);

  useEffect(() => {
    // Start playing BGM when component mounts
    audioManager.playBGM(audioManager.currentTrack);
  }, []);

  const themes = [
    {
      id: 'MILITARY',
      name: 'Military',
      icon: Rocket,
      color: '#10b981',
      bgColor: '#064e3b',
      description: 'Strategic warfare theme'
    },
    {
      id: 'EDUCATION',
      name: 'Education',
      icon: GraduationCap,
      color: '#3b82f6',
      bgColor: '#1e3a8a',
      description: 'Academic learning theme'
    },
    {
      id: 'FANTASY',
      name: 'Fantasy',
      icon: Sword,
      color: '#8b5cf6',
      bgColor: '#581c87',
      description: 'Magical adventure theme'
    }
  ];

  const handleThemeSelect = (theme) => {
    onThemeSelect(theme);
    setShowStoryline(true);
  };

  const handleStartGame = () => {
    if (selectedTheme) {
      onStart();
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center items-center p-4 relative">
      <ThemeBackground theme={selectedTheme} />
      
      {/* Admin Header */}
      {isAdmin && (
        <div className="fixed top-4 left-4 right-4 z-50">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3 bg-red-500/20 backdrop-blur-sm rounded-lg px-4 py-2 border border-red-500/30">
              <Shield size={20} className="text-red-400" />
              <span className="text-white font-medium">Super Admin Mode</span>
            </div>
            <button
              onClick={onLogout}
              className="flex items-center gap-2 bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700/80 text-white rounded-lg px-4 py-2 transition-colors"
            >
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      )}
      
      <div className="relative z-10 max-w-4xl w-full flex-1 flex flex-col justify-center">
        {/* Animated Title */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <AnimatedTitle />
        </motion.div>

        {/* Theme Selection */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {themes.map((theme, index) => {
            const Icon = theme.icon;
            const isSelected = selectedTheme === theme.id;
            
            return (
              <motion.div
                key={theme.id}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative p-6 rounded-xl cursor-pointer transition-all duration-300 ${
                  isSelected 
                    ? 'ring-4 ring-cyan-400 bg-gray-800/90' 
                    : 'bg-gray-800/50 hover:bg-gray-800/70'
                }`}
                onClick={() => handleThemeSelect(theme.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="text-center">
                  <div 
                    className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center"
                    style={{ backgroundColor: theme.bgColor }}
                  >
                    <Icon size={40} style={{ color: theme.color }} />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">{theme.name}</h3>
                  <p className="text-gray-400">{theme.description}</p>
                </div>
                
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-2 right-2 w-6 h-6 bg-cyan-400 rounded-full flex items-center justify-center"
                  >
                    <Check size={16} className="text-gray-900" />
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Start Game Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mb-16"
        >
          <button
            onClick={handleStartGame}
            disabled={!selectedTheme}
            className={`px-8 py-4 rounded-lg text-xl font-bold transition-all duration-300 ${
              selectedTheme
                ? 'bg-cyan-500 hover:bg-cyan-600 text-white'
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
          >
            Start Game
          </button>
          
          {selectedTheme && (
            <div className="mt-4">
              <button
                onClick={() => setShowStoryline(true)}
                className="text-cyan-400 hover:text-cyan-300 underline"
              >
                View Storyline
              </button>
            </div>
          )}
        </motion.div>
      </div>

      {/* Bottom Right Navigation */}
      <div className="fixed bottom-4 right-4 z-50">
        <div className="flex flex-col gap-3">
          <button
            onClick={() => setShowHowToPlay(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800/90 hover:bg-gray-700/90 text-white rounded-lg transition-colors backdrop-blur-sm"
          >
            <HelpCircle size={20} />
            <span className="hidden sm:inline">How to Play</span>
          </button>
          <button
            onClick={() => setShowScoreboard(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800/90 hover:bg-gray-700/90 text-white rounded-lg transition-colors backdrop-blur-sm"
          >
            <Trophy size={20} />
            <span className="hidden sm:inline">Scoreboard</span>
          </button>
          {isAdmin && (
            <button
              onClick={() => setShowQuestionManagement(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-500/80 hover:bg-red-600/80 text-white rounded-lg transition-colors backdrop-blur-sm"
            >
              <FileText size={20} />
              <span className="hidden sm:inline">Manage Questions</span>
            </button>
          )}
          <button
            onClick={() => setShowAudioSettings(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800/90 hover:bg-gray-700/90 text-white rounded-lg transition-colors backdrop-blur-sm"
          >
            <Settings size={20} />
            <span className="hidden sm:inline">Settings</span>
          </button>
        </div>
      </div>

      <HowToPlayModal 
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
      />

      <AudioSettings 
        isOpen={showAudioSettings}
        onClose={() => setShowAudioSettings(false)}
      />

      <Scoreboard 
        isOpen={showScoreboard}
        onClose={() => setShowScoreboard(false)}
        isAdmin={isAdmin}
      />

      <StorylineModal
        isOpen={showStoryline}
        onClose={() => setShowStoryline(false)}
        theme={selectedTheme}
      />

      {isAdmin && (
        <QuestionManagementModal
          isOpen={showQuestionManagement}
          onClose={() => setShowQuestionManagement(false)}
        />
      )}

      <Footer />
    </div>
  );
};

const GameInterface = ({ theme, onBack, gameMode, cefrLevel, partOfSpeech, playerInfo }) => {
  const [timeLeft, setTimeLeft] = useState(CONSTANTS.GAME_DURATION);
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [gameState, setGameState] = useState('playing'); // playing, paused, finished
  const [skipsLeft, setSkipsLeft] = useState(CONSTANTS.SKIP_SYSTEM.MAX_SKIPS);
  const [showHint, setShowHint] = useState(false);
  const [powerUps, setPowerUps] = useState({});
  const [showAudioSettings, setShowAudioSettings] = useState(false);
  const [comboEffect, setComboEffect] = useState(null);
  const [showStoryline, setShowStoryline] = useState(false);
  
  // Level-up mode specific states
  const [currentLevel, setCurrentLevel] = useState(
    gameMode === 'LEVEL_UP' ? CONSTANTS.LEVEL_UP_MODE.STARTING_LEVEL : cefrLevel
  );
  const [correctAnswersCount, setCorrectAnswersCount] = useState(0);
  const [levelUpEffect, setLevelUpEffect] = useState(null);

  // Generate a question based on game mode and current level
  const generateQuestion = useCallback(() => {
    const level = gameMode === 'LEVEL_UP' ? currentLevel : cefrLevel;
    
    // Try to get a question from the question bank first
    const question = questionBankManager.getRandomQuestion(gameMode, level);
    
    if (question) {
      return question;
    }

    // Fallback to sample questions if no questions in bank
    const sampleQuestions = {
      SYNONYM: {
        word: 'happy',
        hint: 'A feeling of joy or contentment',
        correctAnswers: ['joyful', 'cheerful', 'glad', 'pleased', 'content'],
        level: level
      },
      GRAMMAR: {
        sentence: 'She don\'t like coffee.',
        hint: 'Check subject-verb agreement',
        correctAnswer: 'She doesn\'t like coffee.',
        rule: 'SUBJECT_VERB_AGREEMENT',
        level: level
      },
      PASSIVE_ACTIVE: {
        sentence: 'The cat chased the mouse.',
        hint: 'Convert to passive voice',
        correctAnswer: 'The mouse was chased by the cat.',
        level: level
      },
      PART_OF_SPEECH: {
        word: 'happy',
        from: 'ADJECTIVE',
        to: 'NOUN',
        hint: 'Convert adjective to noun',
        correctAnswer: 'happiness',
        level: level
      },
      LEVEL_UP: {
        word: 'happy',
        hint: 'A feeling of joy or contentment',
        correctAnswers: ['joyful', 'cheerful', 'glad', 'pleased', 'content'],
        level: level
      }
    };

    return sampleQuestions[gameMode] || sampleQuestions.SYNONYM;
  }, [gameMode, cefrLevel, currentLevel]);

  // Initialize first question
  useEffect(() => {
    setCurrentQuestion(generateQuestion());
  }, [generateQuestion]);

  // Update BGM speed based on current level in level-up mode
  useEffect(() => {
    if (gameMode === 'LEVEL_UP') {
      const speedMultiplier = CONSTANTS.LEVEL_UP_MODE.BGM_SPEED_MULTIPLIERS[currentLevel];
      audioManager.updateBGMSpeed(speedMultiplier);
    }
  }, [currentLevel, gameMode]);

  // Timer logic
  useEffect(() => {
    if (gameState !== 'playing' || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setGameState('finished');
          audioManager.playGameOverBGM();
          
          // Save score to scoreboard
          const scoreData = {
            score,
            theme,
            gameMode,
            cefrLevel: gameMode === 'LEVEL_UP' ? currentLevel : cefrLevel,
            combo,
            playerName: playerInfo?.name || '',
            playerClass: playerInfo?.class || ''
          };
          
          if (gameMode === 'LEVEL_UP') {
            scoreData.finalLevel = currentLevel;
            scoreData.levelsProgressed = CONSTANTS.LEVEL_UP_MODE.LEVEL_PROGRESSION.indexOf(currentLevel) + 1;
          }
          
          scoreboardManager.saveScore(scoreData);
          
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameState, timeLeft, score, theme, gameMode, cefrLevel, combo, currentLevel, playerInfo]);

  const handleLevelUp = () => {
    const currentIndex = CONSTANTS.LEVEL_UP_MODE.LEVEL_PROGRESSION.indexOf(currentLevel);
    if (currentIndex < CONSTANTS.LEVEL_UP_MODE.LEVEL_PROGRESSION.length - 1) {
      const newLevel = CONSTANTS.LEVEL_UP_MODE.LEVEL_PROGRESSION[currentIndex + 1];
      setCurrentLevel(newLevel);
      setLevelUpEffect(newLevel);
      audioManager.playSFX(CONSTANTS.AUDIO.SFX_EVENTS.LEVEL_UP);
    }
  };

  const handleAnswer = (answer) => {
    if (!currentQuestion || gameState !== 'playing') return;

    let isCorrect = false;
    
    // Check answer based on game mode
    switch (gameMode) {
      case 'SYNONYM':
      case 'LEVEL_UP':
        isCorrect = currentQuestion.correctAnswers.some(correct => 
          correct.toLowerCase() === answer.toLowerCase().trim()
        );
        break;
      case 'GRAMMAR':
      case 'PASSIVE_ACTIVE':
        isCorrect = currentQuestion.correctAnswer.toLowerCase() === answer.toLowerCase().trim();
        break;
      case 'PART_OF_SPEECH':
        isCorrect = currentQuestion.correctAnswer.toLowerCase() === answer.toLowerCase().trim();
        break;
    }

    if (isCorrect) {
      const newCombo = combo + 1;
      setCombo(newCombo);
      
      // Level-up mode specific logic
      if (gameMode === 'LEVEL_UP') {
        const newCorrectCount = correctAnswersCount + 1;
        setCorrectAnswersCount(newCorrectCount);
        
        // Check if player should level up
        if (newCorrectCount % CONSTANTS.LEVEL_UP_MODE.ANSWERS_PER_LEVEL === 0) {
          handleLevelUp();
        }
      }
      
      // Play correct answer sound
      audioManager.playSFX(CONSTANTS.AUDIO.SFX_EVENTS.CORRECT_ANSWER);
      
      // Trigger combo effect for significant combos
      if (newCombo >= 3 && (newCombo === 3 || newCombo === 5 || newCombo === 7 || newCombo >= 10)) {
        setComboEffect(newCombo);
        audioManager.playSFX(CONSTANTS.AUDIO.SFX_EVENTS.COMBO_ACHIEVED);
      }
      
      // Calculate points
      let points = CONSTANTS.POINTS.BASE;
      if (gameMode === 'GRAMMAR') points = CONSTANTS.POINTS.GRAMMAR_BASE;
      else if (gameMode === 'PASSIVE_ACTIVE') points = CONSTANTS.POINTS.PASSIVE_ACTIVE_BASE;
      else if (gameMode === 'PART_OF_SPEECH') points = CONSTANTS.POINTS.PART_OF_SPEECH_BASE;
      
      // Add combo bonus
      if (newCombo >= CONSTANTS.COMBO_SYSTEM.COMBO_BONUS_THRESHOLD) {
        const bonus = CONSTANTS.COMBO_SYSTEM.COMBO_BONUSES[newCombo] || 400;
        points += bonus;
      }

      setScore(prev => prev + points);
    } else {
      setCombo(0);
      audioManager.playSFX(CONSTANTS.AUDIO.SFX_EVENTS.INCORRECT_ANSWER);
    }

    // Generate next question
    setCurrentQuestion(generateQuestion());
    setUserAnswer('');
    setShowHint(false);
  };

  const handleSkip = () => {
    if (skipsLeft > 0) {
      setSkipsLeft(prev => prev - 1);
      setScore(prev => Math.max(0, prev - CONSTANTS.SKIP_SYSTEM.PENALTY));
      audioManager.playSFX(CONSTANTS.AUDIO.SFX_EVENTS.SKIP_USED);
      setCurrentQuestion(generateQuestion());
      setUserAnswer('');
      setShowHint(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAnswer(userAnswer);
    }
  };

  const handleBackToMenu = () => {
    // Stop game over BGM and resume normal BGM
    audioManager.playBGM(audioManager.currentTrack);
    onBack();
  };

  const handlePlayAgain = () => {
    // Stop game over BGM and resume normal BGM
    audioManager.playBGM(audioManager.currentTrack);
    window.location.reload();
  };

  if (gameState === 'finished') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <ThemeBackground theme={theme} gameMode={gameMode} currentLevel={currentLevel} />
        <div className="relative z-10 max-w-2xl w-full text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gray-800/90 rounded-xl p-8"
          >
            <Trophy size={80} className="text-yellow-400 mx-auto mb-4" />
            <h2 className="text-4xl font-bold text-white mb-4">Game Over!</h2>
            <div className="text-2xl text-cyan-400 mb-6">Final Score: {score}</div>
            <div className="space-y-2 text-gray-300 mb-8">
              {playerInfo?.name && (
                <p className="flex items-center justify-center gap-2">
                  <User size={16} />
                  Player: {playerInfo.name}
                  {playerInfo.class && (
                    <>
                      <span className="text-gray-500">•</span>
                      <School size={16} />
                      {playerInfo.class}
                    </>
                  )}
                </p>
              )}
              <p>Theme: {theme}</p>
              <p>Mode: {gameMode}</p>
              {gameMode === 'LEVEL_UP' ? (
                <>
                  <p>Final Level: {currentLevel}</p>
                  <p>Levels Progressed: {CONSTANTS.LEVEL_UP_MODE.LEVEL_PROGRESSION.indexOf(currentLevel) + 1}</p>
                </>
              ) : (
                <p>Level: {cefrLevel}</p>
              )}
              <p>Best Combo: {combo}</p>
            </div>
            <div className="flex gap-4 justify-center">
              <button
                onClick={handlePlayAgain}
                className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-bold"
              >
                Play Again
              </button>
              <button
                onClick={handleBackToMenu}
                className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-bold"
              >
                Back to Menu
              </button>
            </div>
          </motion.div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4">
      <ThemeBackground theme={theme} gameMode={gameMode} currentLevel={currentLevel} />
      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800/80 hover:bg-gray-700/80 text-white rounded-lg"
          >
            <ArrowLeft size={20} />
            Back
          </button>
          
          <div className="flex items-center gap-6 text-white">
            <div className="flex items-center gap-2">
              <Clock size={20} className="text-cyan-400" />
              <span className="text-xl font-bold">{timeLeft}s</span>
            </div>
            <div className="flex items-center gap-2">
              <Star size={20} className="text-yellow-400" />
              <span className="text-xl font-bold">{score}</span>
            </div>
            <div className="flex items-center gap-2">
              <Zap size={20} className="text-purple-400" />
              <span className="text-xl font-bold">{combo}x</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowStoryline(true)}
                className="p-2 bg-gray-800/80 hover:bg-gray-700/80 text-white rounded-lg"
                title="View Storyline"
              >
                <Book size={20} />
              </button>
              <button
                onClick={() => setShowAudioSettings(true)}
                className="p-2 bg-gray-800/80 hover:bg-gray-700/80 text-white rounded-lg"
                title="Audio Settings"
              >
                <Settings size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Player Info Display */}
        {playerInfo?.name && (
          <div className="bg-gray-800/90 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-center gap-4 text-white">
              <div className="flex items-center gap-2">
                <User size={20} className="text-cyan-400" />
                <span className="font-medium">{playerInfo.name}</span>
              </div>
              {playerInfo.class && (
                <>
                  <span className="text-gray-500">•</span>
                  <div className="flex items-center gap-2">
                    <School size={20} className="text-green-400" />
                    <span className="font-medium">{playerInfo.class}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Level-up mode progress indicator */}
        {gameMode === 'LEVEL_UP' && (
          <div className="bg-gray-800/90 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <TrendingUp size={24} className="text-cyan-400" />
                <div>
                  <div className="text-xl font-bold text-white">
                    Current Level: {currentLevel} - {CONSTANTS.CEFR_DESCRIPTIONS[currentLevel]}
                  </div>
                  <div className="text-sm text-gray-400">
                    Progress to next level: {correctAnswersCount % CONSTANTS.LEVEL_UP_MODE.ANSWERS_PER_LEVEL}/{CONSTANTS.LEVEL_UP_MODE.ANSWERS_PER_LEVEL}
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <motion.div
                className="bg-cyan-400 h-2 rounded-full"
                style={{
                  width: `${(correctAnswersCount % CONSTANTS.LEVEL_UP_MODE.ANSWERS_PER_LEVEL) / CONSTANTS.LEVEL_UP_MODE.ANSWERS_PER_LEVEL * 100}%`
                }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>
        )}

        {/* Question Area */}
        <div className="bg-gray-800/90 rounded-xl p-8 mb-6">
          <div className="text-center mb-6">
            <div className="text-sm text-gray-400 mb-2">
              {gameMode} • {gameMode === 'LEVEL_UP' ? currentLevel : cefrLevel} Level
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">
              {(gameMode === 'SYNONYM' || gameMode === 'LEVEL_UP') && `Find a synonym for: "${currentQuestion?.word}"`}
              {gameMode === 'GRAMMAR' && `Correct the grammar: "${currentQuestion?.sentence}"`}
              {gameMode === 'PASSIVE_ACTIVE' && `Convert to passive voice: "${currentQuestion?.sentence}"`}
              {gameMode === 'PART_OF_SPEECH' && `Convert "${currentQuestion?.word}" from ${currentQuestion?.from} to ${currentQuestion?.to}`}
            </h2>
            
            {showHint && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-cyan-400 mb-4"
              >
                💡 {currentQuestion?.hint}
              </motion.div>
            )}
          </div>

          <div className="max-w-md mx-auto">
            <input
              type="text"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your answer..."
              className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-cyan-400 focus:outline-none text-lg"
              autoFocus
            />
            
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => handleAnswer(userAnswer)}
                className="flex-1 px-4 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-bold"
              >
                Submit
              </button>
              <button
                onClick={() => setShowHint(!showHint)}
                className="px-4 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg"
              >
                <HelpCircle size={20} />
              </button>
              <button
                onClick={handleSkip}
                disabled={skipsLeft === 0}
                className={`px-4 py-3 rounded-lg ${
                  skipsLeft > 0 
                    ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
              >
                <SkipForward size={20} />
                <span className="ml-1 text-sm">{skipsLeft}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-800/80 rounded-lg p-4 text-center">
            <div className="text-cyan-400 text-2xl font-bold">{score}</div>
            <div className="text-gray-400 text-sm">Score</div>
          </div>
          <div className="bg-gray-800/80 rounded-lg p-4 text-center">
            <div className="text-purple-400 text-2xl font-bold">{combo}</div>
            <div className="text-gray-400 text-sm">Combo</div>
          </div>
          <div className="bg-gray-800/80 rounded-lg p-4 text-center">
            <div className="text-orange-400 text-2xl font-bold">{skipsLeft}</div>
            <div className="text-gray-400 text-sm">Skips Left</div>
          </div>
          <div className="bg-gray-800/80 rounded-lg p-4 text-center">
            <div className="text-green-400 text-2xl font-bold">{timeLeft}</div>
            <div className="text-gray-400 text-sm">Time Left</div>
          </div>
        </div>
      </div>

      {/* Level-Up Effect */}
      <AnimatePresence>
        {levelUpEffect && (
          <LevelUpEffect
            newLevel={levelUpEffect}
            onComplete={() => setLevelUpEffect(null)}
          />
        )}
      </AnimatePresence>

      {/* Combo Effect */}
      <AnimatePresence>
        {comboEffect && (
          <ComboEffect
            combo={comboEffect}
            onComplete={() => setComboEffect(null)}
          />
        )}
      </AnimatePresence>

      <AudioSettings 
        isOpen={showAudioSettings}
        onClose={() => setShowAudioSettings(false)}
      />

      <StorylineModal
        isOpen={showStoryline}
        onClose={() => setShowStoryline(false)}
        theme={theme}
      />

      <Footer />
    </div>
  );
};

const GameModeSelector = ({ onModeSelect, onBack, selectedTheme }) => {
  const modes = [
    {
      id: 'SYNONYM',
      name: 'Synonym',
      icon: Type,
      description: 'Find words with similar meanings',
      color: '#3b82f6'
    },
    {
      id: 'GRAMMAR',
      name: 'Grammar',
      icon: BookOpen,
      description: 'Correct grammar mistakes',
      color: '#10b981'
    },
    {
      id: 'PASSIVE_ACTIVE',
      name: 'Passive/Active',
      icon: RefreshCw,
      description: 'Convert between voice forms',
      color: '#8b5cf6'
    },
    {
      id: 'PART_OF_SPEECH',
      name: 'Part of Speech',
      icon: Shuffle,
      description: 'Transform word types',
      color: '#f59e0b'
    },
    {
      id: 'LEVEL_UP',
      name: 'Level-Up',
      icon: TrendingUp,
      description: 'Progress through CEFR levels',
      color: '#ef4444'
    }
  ];

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <ThemeBackground theme={selectedTheme} />
      <div className="relative z-10 max-w-4xl w-full">
        <div className="flex items-center mb-8">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800/80 hover:bg-gray-700/80 text-white rounded-lg mr-4"
          >
            <ArrowLeft size={20} />
            Back
          </button>
          <h2 className="text-4xl font-bold text-white">Choose Game Mode</h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modes.map((mode, index) => {
            const Icon = mode.icon;
            
            return (
              <motion.div
                key={mode.id}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-800/80 hover:bg-gray-800/90 p-6 rounded-xl cursor-pointer transition-all duration-300"
                onClick={() => onModeSelect(mode.id)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: mode.color + '20' }}
                  >
                    <Icon size={24} style={{ color: mode.color }} />
                  </div>
                  <h3 className="text-2xl font-bold text-white">{mode.name}</h3>
                </div>
                <p className="text-gray-400">{mode.description}</p>
                {mode.id === 'LEVEL_UP' && (
                  <div className="mt-3 text-sm text-cyan-400">
                    Start at A1 and progress through all CEFR levels!
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
      <Footer />
    </div>
  );
};

const LevelSelector = ({ onLevelSelect, onBack, selectedTheme }) => {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <ThemeBackground theme={selectedTheme} />
      <div className="relative z-10 max-w-4xl w-full">
        <div className="flex items-center mb-8">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800/80 hover:bg-gray-700/80 text-white rounded-lg mr-4"
          >
            <ArrowLeft size={20} />
            Back
          </button>
          <h2 className="text-4xl font-bold text-white">Choose CEFR Level</h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {CONSTANTS.CEFR_LEVELS.map((level, index) => (
            <motion.div
              key={level}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-gray-800/80 hover:bg-gray-800/90 p-6 rounded-xl cursor-pointer transition-all duration-300"
              onClick={() => onLevelSelect(level)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="text-center">
                <div className="text-3xl font-bold text-cyan-400 mb-2">{level}</div>
                <div className="text-xl text-white mb-2">{CONSTANTS.CEFR_DESCRIPTIONS[level]}</div>
                <div className="text-gray-400">
                  {level === 'A1' && 'Basic phrases and expressions'}
                  {level === 'A2' && 'Simple everyday situations'}
                  {level === 'B1' && 'Clear standard language'}
                  {level === 'B2' && 'Complex texts and ideas'}
                  {level === 'C1' && 'Flexible and effective use'}
                  {level === 'C2' && 'Precise and nuanced expression'}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
};

function App() {
  const [currentScreen, setCurrentScreen] = useState('player-info');
  const [selectedTheme, setSelectedTheme] = useState('');
  const [selectedMode, setSelectedMode] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedPartOfSpeech, setSelectedPartOfSpeech] = useState('ANY');
  const [playerInfo, setPlayerInfo] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const handlePlayerInfoComplete = (info) => {
    setPlayerInfo(info);
    setIsAdmin(info.isAdmin || false);
    setCurrentScreen('theme-select');
  };

  const handleThemeSelect = (theme) => {
    setSelectedTheme(theme);
  };

  const handleStart = () => {
    if (selectedTheme) {
      setCurrentScreen('mode-select');
    }
  };

  const handleModeSelect = (mode) => {
    setSelectedMode(mode);
    if (mode === 'LEVEL_UP') {
      // Skip level selection for level-up mode
      setCurrentScreen('game');
    } else {
      setCurrentScreen('level-select');
    }
  };

  const handleLevelSelect = (level) => {
    setSelectedLevel(level);
    setCurrentScreen('game');
  };

  const handleLogout = () => {
    adminManager.logout();
    setIsAdmin(false);
    setPlayerInfo(null);
    setCurrentScreen('player-info');
    setSelectedTheme('');
    setSelectedMode('');
    setSelectedLevel('');
  };

  const handleBackToPlayerInfo = () => {
    setCurrentScreen('player-info');
    setSelectedTheme('');
    setSelectedMode('');
    setSelectedLevel('');
  };

  const handleBackToThemes = () => {
    setCurrentScreen('theme-select');
    setSelectedTheme('');
    setSelectedMode('');
    setSelectedLevel('');
  };

  const handleBackToModes = () => {
    setCurrentScreen('mode-select');
    setSelectedMode('');
    setSelectedLevel('');
  };

  const handleBackToLevels = () => {
    setCurrentScreen('level-select');
    setSelectedLevel('');
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <AnimatePresence mode="wait">
        {currentScreen === 'player-info' && (
          <motion.div
            key="player-info"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="min-h-screen flex items-center justify-center">
              <ThemeBackground />
              <PlayerInfoModal
                isOpen={true}
                onComplete={handlePlayerInfoComplete}
              />
              <Footer />
            </div>
          </motion.div>
        )}

        {currentScreen === 'theme-select' && (
          <motion.div
            key="theme-select"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ThemeSelector
              selectedTheme={selectedTheme}
              onThemeSelect={handleThemeSelect}
              onStart={handleStart}
              isAdmin={isAdmin}
              onLogout={handleLogout}
            />
          </motion.div>
        )}

        {currentScreen === 'mode-select' && (
          <motion.div
            key="mode-select"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <GameModeSelector
              onModeSelect={handleModeSelect}
              onBack={handleBackToThemes}
              selectedTheme={selectedTheme}
            />
          </motion.div>
        )}

        {currentScreen === 'level-select' && (
          <motion.div
            key="level-select"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <LevelSelector
              onLevelSelect={handleLevelSelect}
              onBack={handleBackToModes}
              selectedTheme={selectedTheme}
            />
          </motion.div>
        )}

        {currentScreen === 'game' && (
          <motion.div
            key="game"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <GameInterface
              theme={selectedTheme}
              gameMode={selectedMode}
              cefrLevel={selectedLevel}
              partOfSpeech={selectedPartOfSpeech}
              playerInfo={playerInfo}
              onBack={selectedMode === 'LEVEL_UP' ? handleBackToModes : handleBackToLevels}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
